import { useCallback, useEffect, useMemo, useState } from "react";
import { useUser } from "../../context/userContext";
import ReusableTable from "./reusableTable";
import { convertDateTimeToDateString, handleDisplayCreatedOnOrUpdatedOn } from "../../utils/datetimeUtils";
import { handlePriceWithRM } from "../../utils/priceUtils";
import handleAgentName from "../../utils/userUtils";

export default function GenericSalesList({
    fetchDataApi,
    ModalComponent,
    modalExtraProps = {},
    onCreate
}) {
    const { user } = useUser();
    const [sales, setSales] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showSalesModal, setShowSalesModal] = useState(false);
    const [selectedSale, setSelectedSale] = useState(null);

    const fetchSalesData = useCallback(async () => {
        try {
            let response;

            if (fetchDataApi.length === 0) {
                response = await fetchDataApi();
            }
            else {
                response = await fetchDataApi(user.userId);
            }
            setSales(response.data);
        }
        catch (error) {
            console.error("Error fetching sales data:", error);
        }
        finally {
            setLoading(false);
        }
    }, [fetchDataApi, user?.userId]);

    useEffect(() => {
        fetchSalesData();
    }, [fetchSalesData]);

    const handleViewSalesModal = (sales) => async () => {
        setSelectedSale(sales);
        setShowSalesModal(true);
    };

    const handleCloseSalesModal = () => {
        setShowSalesModal(false);
        setSelectedSale(null);
    };

    const columns = useMemo(() => [
        {
            id: 'salesId',
            header: 'Sales ID',
            accessorKey: 'salesId',
        },
        {
            id: 'depositDate',
            header: 'Deposit Date',
            // accessorFn: row => convertDateTimeToDateString(row.depositOn),
            accessorFn: row => row.depositOn ? new Date(row.depositOn).getTime() : null,
            Cell: ({ row }) => convertDateTimeToDateString(row.original.depositOn),
        },
        {
            id: "customerName",
            header: 'Customer',
            accessorKey: 'customerDetails.customerName',
        },
        {
            id: 'ProductEnergy',
            header: 'Product Energy',
            accessorFn: row =>
                `${row.energy ?? ""} kWp`.trim(),
        },
        {
            id: 'agent',
            header: 'Agent',
            accessorFn: row => {
                const name = handleAgentName(row.agentDetails.firstName, row.agentDetails.lastName, row.agentDetails.alias);
                return `${row.agentDetails?.groupName ?? ""} - ${name}`.trim();
            },
        },
        {
            id: 'coBroke',
            header: 'Co-broke',
            accessorFn: row => {
                if (!row.coAgentDetails) {
                    return "";
                }
                
                const name = handleAgentName(row.coAgentDetails.firstName, row.coAgentDetails.lastName, row.coAgentDetails.alias);
                return `${row.coAgentDetails?.groupName ?? ""} - ${name}`.trim();
            },
        },
        {
            id: 'oriPrice',
            header: 'Ori Price',
            accessorFn: row => row.originalPrice,
            Cell: ({ row }) => {
                const cp = row.original;
                if (!cp || cp.originalPrice == null) {
                    return "";
                }

                return handlePriceWithRM(cp.originalPrice);
            },
        },
        {
            id: 'asternDiscount',
            header: 'Astern Disc.',
            accessorFn: row => row.discountByAstern,
            Cell: ({ row }) => {
                const cp = row.original;
                if (!cp || cp.discountByAstern == null) {
                    return "";
                };

                return handlePriceWithRM(cp.discountByAstern);
            },
        },
        {
            id: 'agentDiscount',
            header: 'Agent Disc.',
            accessorFn: row => row.discountByAgent,
            Cell: ({ row }) => {
                const cp = row.original;
                if (!cp || cp.discountByAgent == null) {
                    return "";
                };

                return handlePriceWithRM(cp.discountByAgent);
            },
        },
        {
            id: 'finalPrice',
            header: 'Final Price',
            accessorFn: row => row.finalPrice,
            Cell: ({ row }) => {
                const cp = row.original;
                if (!cp || cp.finalPrice == null) {
                    return null
                };

                return handlePriceWithRM(
                    cp.finalPrice
                );
            },
        },
        {
            id: "status",
            header: "Status",
            accessorKey: "status",
        },
        {
            id: "date",
            header: 'Date',
            filterVariant: 'datetime-range',
            accessorFn: (row) => new Date(handleDisplayCreatedOnOrUpdatedOn(row.createdOn, row.updatedOn)),
            Cell: ({ row }) => {
                const cp = row.original;
                if (!cp) {
                    return "";
                }
                return convertDateTimeToDateString(handleDisplayCreatedOnOrUpdatedOn(cp.createdOn, cp.updatedOn));
            },
        },
    ],
        []
    );

    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <ReusableTable
            columns={columns}
            data={sales}
            onRowClick={handleViewSalesModal}
            onCreate={onCreate}
        >
            {ModalComponent && (
                <ModalComponent
                    show={showSalesModal}
                    onHide={handleCloseSalesModal}
                    selectedSale={selectedSale}
                    onSaved={fetchSalesData}
                    {...modalExtraProps}
                />
            )}
        </ReusableTable>
    );
}